import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import type { ValueFormat } from 'shared/src/api/ai-viz-types';
import { formatVizValue } from 'shared/src/utils/ai/ai-viz-transformers';
import { CHART_PALETTE } from './chart-utils';

export type BarSeries = { key: string; name: string };

export type BarLineChartProps = {
  title: string;
  axisFormat: ValueFormat;
  /** Recharts-ready rows: { date: "YYYY-MM-DD", [plannedKey]: number|null, ...barKeys: number|null } */
  data: Array<Record<string, string | number | null>>;
  planned: { key: string; name: string };
  bars: BarSeries[];
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDateLabel(iso: string): string {
  const parts = iso.split('-');
  return `${parts[2]} ${MONTHS[Number(parts[1]) - 1]}`;
}

export function BarLineChart({ title, axisFormat, data, planned, bars }: BarLineChartProps) {
  const tickInterval = Math.max(1, Math.floor(data.length / 12));

  const fmtValue = (v: number) => formatVizValue(v, axisFormat);

  const displayData = data.map(row => ({
    ...row,
    _dateLabel: typeof row.date === 'string' ? formatDateLabel(row.date) : row.date
  }));

  return (
    <div className="w-full bg-white rounded-2xl shadow-lg p-6">
      <h2 className="text-2xl font-semibold mb-4">{title}</h2>

      <ResponsiveContainer width="100%" height={450}>
        <ComposedChart
          data={displayData}
          margin={{ top: 12, right: 16, bottom: 12, left: 6 }}
          barCategoryGap={2}
          barGap={1}>
          <XAxis
            dataKey="_dateLabel"
            interval={tickInterval}
            tickMargin={8}
            tick={{ fontSize: 11 }}
            axisLine={{ stroke: 'rgba(0,0,0,0.35)' }}
            tickLine={{ stroke: 'rgba(0,0,0,0.35)', strokeWidth: 1 }}
          />
          <YAxis
            tickFormatter={fmtValue}
            tick={{ fontSize: 11 }}
            axisLine={{ stroke: 'rgba(0,0,0,0.35)' }}
            tickLine={{ stroke: 'rgba(0,0,0,0.35)', strokeWidth: 1 }}
            width={70}
          />
          <Tooltip
            labelFormatter={(_label, payload) => {
              const first = payload?.[0];
              const row = first?.payload as Record<string, unknown> | undefined;
              return (row?.date as string) ?? '';
            }}
            formatter={value => (typeof value === 'number' ? fmtValue(value) : String(value ?? ''))}
            contentStyle={{
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
            }}
            itemStyle={{ paddingTop: 2, paddingBottom: 2 }}
            labelStyle={{ marginBottom: 6, fontWeight: 600 }}
            cursor={{ fill: 'rgba(0,0,0,0.03)' }}
          />
          <Legend wrapperStyle={{ paddingTop: 6, fontSize: 12 }} iconSize={10} />

          {bars.map((bar, i) => (
            <Bar
              key={bar.key}
              dataKey={bar.key}
              name={bar.name}
              fill={CHART_PALETTE[i % CHART_PALETTE.length]}
              stackId={bars.length > 1 ? 'stack' : undefined}
              barSize={8}
              isAnimationActive={false}
            />
          ))}

          <Line
            type="linear"
            dataKey={planned.key}
            name={planned.name}
            stroke="#374151"
            strokeWidth={1.25}
            strokeDasharray="3 1.5"
            strokeLinecap="butt"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
